import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { API } from "../services/api";
import "./LoginPage.css";

function extractBackendMessage(err) {
    const data = err.response?.data;
    if (typeof data === "string") return data;
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
    if (typeof data?.error === "string" && data.error.trim()) return data.error;
    return null;
}

function getLoginErrorMessage(err) {
    const status = err.response?.status;
    const backendMessage = extractBackendMessage(err);

    if (status === 401) return "Email ou mot de passe incorrect.";
    if (status === 403) return backendMessage || "Accès refusé : droits insuffisants.";
    if (status === 423) return backendMessage || "Compte verrouillé. Contactez un administrateur.";
    if (status === 429) return backendMessage || "Trop de tentatives de connexion. Réessayez dans quelques minutes.";
    if (status === 400) return backendMessage || "Demande de connexion invalide.";
    if (!err.response) return "Impossible de contacter le serveur. Vérifiez votre connexion puis réessayez.";

    return "Erreur serveur, réessayez.";
}

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sessionExpired] = useState(() => {
        const expired = localStorage.getItem("sessionExpired") === "true";
        if (expired) localStorage.removeItem("sessionExpired");
        return expired;
    });

    const from = location.state?.from?.pathname || "/";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const { data } = await API.post("/auth/login", { email, password }, { noRedirectOn401: true });
            // Le JWT est posé en cookie HttpOnly par le serveur ; la réponse ne contient que le profil.
            login(data);
            navigate(from, { replace: true });
        } catch (err) {
            setError(getLoginErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="login-page">
            <section className="login-brand brand-grid" aria-label="Présentation">
                <img
                    className="login-brand__logo"
                    src="/logotype-horizontal-blanc.svg"
                    alt="SUIVI QUALITÉ"
                />
                <div className="login-message">
                    <p className="login-eyebrow">Portail pharmacie</p>
                    <h1>Suivi qualité de votre Pharmacie</h1>
                </div>
            </section>

            <section className="login-panel">
                <div className="login-card">
                    <p className="login-eyebrow">Bienvenue</p>
                    <h2>Connexion à votre espace</h2>

                    {sessionExpired && (
                        <div className="login-notice login-notice--warning" role="status">
                            Votre session a expiré après 2 heures d'inactivité. Veuillez vous reconnecter.
                        </div>
                    )}

                    <form className="login-form" onSubmit={handleSubmit}>
                        <label htmlFor="login-email">Adresse e-mail</label>
                        <input
                            id="login-email"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            autoComplete="email"
                            maxLength={254}
                            required
                            autoFocus
                        />

                        <label htmlFor="login-password">Mot de passe</label>
                        <div className="password-input">
                            <input
                                id="login-password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                autoComplete="current-password"
                                maxLength={64}
                                required
                            />
                            <button
                                type="button"
                                className="password-visibility"
                                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                aria-pressed={showPassword}
                                onClick={() => setShowPassword(current => !current)}
                            >
                                <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12" />
                                    <circle cx="12" cy="12" r="2.5" />
                                    {showPassword && <path d="M4 4l16 16" />}
                                </svg>
                            </button>
                        </div>

                        {error && (
                            <div className="login-notice login-notice--error" role="alert">
                                {error}
                            </div>
                        )}

                        <button
                            className="login-submit"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? "Connexion..." : "Se connecter"}
                        </button>
                    </form>
                </div>
                <p className="login-legal">
                    SUIVI QUALITÉ Pharmacie
                </p>
            </section>
        </main>
    );
}
